import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import {
  DeleteSharedItemDocument,
  RateSharedItemDocument,
  SharedItemDetailDocument,
  SharedItemFileContentsDocument,
  SignUpSharedProjectDocument,
  UpdateSharedItemDocument,
  type SharedItemDetailQuery,
} from "@/graphql/generated/operations";
import { base64ToBlob } from "@/lib/use-open-document";
import { downloadBlob } from "@/lib/file-download";

export type SharedItem = NonNullable<SharedItemDetailQuery["sharedItem"]>;

export interface SharedItemFormInput {
  name: string;
  shortDescription: string;
  description: string;
}

export interface UseSharedItemResult {
  item: SharedItem | null;
  loading: boolean;
  notFound: boolean;
  error: Error | null;
  busy: boolean;
  update: (input: SharedItemFormInput) => Promise<void>;
  remove: () => Promise<void>;
  rate: (fiveStarRating: number) => Promise<void>;
  signUp: (signedUp: boolean) => Promise<void>;
  downloadFile: (fileId: string) => Promise<void>;
  openFile: (fileId: string) => Promise<void>;
  refetch: () => Promise<unknown>;
}

export function useSharedItem(itemId: string): UseSharedItemResult {
  const query = useQuery(SharedItemDetailDocument, {
    variables: { id: itemId },
    skip: !itemId,
  });
  const [updateMut, updateState] = useMutation(UpdateSharedItemDocument);
  const [deleteMut, deleteState] = useMutation(DeleteSharedItemDocument);
  const [rateMut, rateState] = useMutation(RateSharedItemDocument);
  const [signUpMut, signUpState] = useMutation(SignUpSharedProjectDocument);
  const apollo = useApolloClient();

  const item = query.data?.sharedItem ?? null;

  async function update(input: SharedItemFormInput) {
    await updateMut({
      variables: {
        id: itemId,
        input: {
          data: {
            name: input.name.trim(),
            shortDescription: input.shortDescription,
            description: input.description,
          },
          dryRun: false,
        },
      },
    });
    await query.refetch();
  }

  async function remove() {
    await deleteMut({
      variables: { input: { id: itemId } },
      update(cache) {
        cache.evict({ id: cache.identify({ __typename: "SharedItem", id: itemId }) });
        // Force any active /share or /save list to re-resolve sharedItems
        // from the network — the entity eviction alone leaves a dangling
        // ref in the list nodes.
        cache.evict({ fieldName: "sharedItems" });
        cache.gc();
      },
    });
  }

  async function rate(fiveStarRating: number) {
    await rateMut({
      variables: { input: { sharedItemId: itemId, fiveStarRating } },
    });
    await query.refetch();
  }

  async function signUp(signedUp: boolean) {
    await signUpMut({
      variables: { input: { sharedItemId: itemId, signedUp } },
    });
    await query.refetch();
  }

  // Attachment bytes are not part of SharedItemDetail (that would pull every
  // file's Binary on each page view). Fetch them on demand.
  async function fetchFileContent(fileId: string) {
    const { data } = await apollo.query({
      query: SharedItemFileContentsDocument,
      variables: { id: itemId },
      fetchPolicy: "network-only",
    });
    const file = data?.sharedItem?.files?.find((f) => f?.id === fileId);
    if (!file?.content) {
      throw new Error("shared item attachment has no downloadable content");
    }
    return {
      content: file.content,
      mimeType: file.mimeType ?? "application/octet-stream",
      fileName: file.fileName,
      fileExtension: file.fileExtension,
    };
  }

  async function downloadFile(fileId: string) {
    const file = await fetchFileContent(fileId);
    const blob = base64ToBlob(file.content, file.mimeType);
    const ext = file.fileExtension?.replace(/^\./, "");
    downloadBlob(blob, file.fileName ?? (ext ? `download.${ext}` : "download"));
  }

  // Preview without forcing a download: open the file's blob in a new tab so
  // the browser can render PDFs/images inline. The tab is opened synchronously
  // inside the click gesture (before the awaited fetch), otherwise the browser
  // treats the later window.open as a blocked pop-up (same reason as useOpenDocument).
  async function openFile(fileId: string) {
    const win = window.open("", "_blank");
    try {
      const file = await fetchFileContent(fileId);
      const url = URL.createObjectURL(base64ToBlob(file.content, file.mimeType));
      if (!win) throw new Error("pop-up blocked");
      win.location.href = url;
      // The tab keeps its own reference; revoke after a grace period.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      win?.close();
      throw e;
    }
  }

  return {
    item,
    loading: query.loading && !query.data,
    notFound: !query.loading && !item,
    error:
      (query.error as Error | undefined) ??
      (updateState.error as Error | undefined) ??
      (deleteState.error as Error | undefined) ??
      (rateState.error as Error | undefined) ??
      (signUpState.error as Error | undefined) ??
      null,
    busy:
      updateState.loading ||
      deleteState.loading ||
      rateState.loading ||
      signUpState.loading,
    update,
    remove,
    rate,
    signUp,
    downloadFile,
    openFile,
    refetch: () => query.refetch(),
  };
}
